// src/components/AdminPanel.js
import React, { useEffect, useState } from "react";
import {
  collection, onSnapshot, addDoc, deleteDoc, doc,
  updateDoc, serverTimestamp, setDoc, writeBatch,
} from "firebase/firestore";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { uploadImageToCloudinary, sizedUrl } from "../utils/uploadImage";
import {
  uploadVideoToCloudinary, optimizeVideoUrl, videoPosterUrl, MAX_VIDEO_MB,
} from "../utils/uploadVideo";
import {
  uploadDocumentToCloudinary, validateDocument, MAX_FILE_MB,
} from "../utils/uploadFile";
import "./AdminPanel.css";

const DEFAULT_FEES = [
  ["Playgroup", 5000, 2000], ["Nursery", 5000, 2000], ["Prep", 5000, 2000],
  ["One", 5000, 2500], ["Two", 5000, 2500], ["Three", 5000, 2500],
  ["Four", 5000, 2500], ["Five", 5000, 2500], ["Six", 5000, 3000],
  ["Seven", 5000, 3000], ["Eight", 5000, 3000], ["Nine", 5000, 3000],
  ["Tenth", 5000, 3000], ["1st & 2nd Year", 5000, 5000],
];

const CAREER_CATEGORIES = ["Teaching", "Administration", "Support Staff"];
const DEFAULT_CAREER_LOCATION = "Chowk Shah Abbas, Suraj Kund Road, Multan";

// Preview URL for a selected file (valid hook, called at top level)
function usePreview(file) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!file) { setUrl(""); return; }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return url;
}

const sortByCreatedDesc = (list) =>
  list.sort((a, b) => {
    const dateA = a.createdAt?.toDate?.() || new Date(0);
    const dateB = b.createdAt?.toDate?.() || new Date(0);
    return dateB - dateA;
  });

function AdminPanel() {
  const [activeTab, setActiveTab] = useState("admissions");

  // ─── State ───
  const [admissions, setAdmissions]         = useState([]);
  const [news, setNews]                     = useState([]);
  const [results, setResults]               = useState([]);
  const [ticker, setTicker]                 = useState([]);
  const [gallery, setGallery]               = useState([]);
  const [admissionsOpen, setAdmissionsOpen] = useState(true);

  // Fees
  const [fees, setFees]                 = useState([]);
  const [feeClass, setFeeClass]         = useState("");
  const [feeAdmission, setFeeAdmission] = useState("");
  const [feeMonthly, setFeeMonthly]     = useState("");
  const [feeOrder, setFeeOrder]         = useState("");
  const [editFeeId, setEditFeeId]       = useState(null);
  const [feeSaving, setFeeSaving]       = useState(false);

  // Admission Form
  const [formInfo, setFormInfo]           = useState(null);
  const [formFile, setFormFile]           = useState(null);
  const [formUploading, setFormUploading] = useState(false);

  // News
  const [newNews, setNewNews]       = useState("");
  const [editNewsId, setEditNewsId] = useState(null);

  // Results
  const [resYear, setResYear]           = useState("");
  const [resName, setResName]           = useState("");
  const [resRoll, setResRoll]           = useState("");
  const [resMarks, setResMarks]         = useState("");
  const [editResultId, setEditResultId] = useState(null);

  // Ticker
  const [newTicker, setNewTicker]       = useState("");
  const [editTickerId, setEditTickerId] = useState(null);

  // Gallery
  const [galleryFile, setGalleryFile]   = useState(null);
  const [galleryTitle, setGalleryTitle] = useState("");
  const [uploading, setUploading]       = useState(false);
  const galleryPreview = usePreview(galleryFile);

  // Campus
  const [campus, setCampus]                   = useState([]);
  const [campusFile, setCampusFile]           = useState(null);
  const [campusTitle, setCampusTitle]         = useState("");
  const [campusUploading, setCampusUploading] = useState(false);
  const campusPreview = usePreview(campusFile);

  // School Life
  const [schoolLife, setSchoolLife]       = useState([]);
  const [lifeFile, setLifeFile]           = useState(null);
  const [lifeTitle, setLifeTitle]         = useState("");
  const [lifeUploading, setLifeUploading] = useState(false);
  const lifePreview = usePreview(lifeFile);

  // Banner
  const [banners, setBanners]                 = useState([]);
  const [bannerFile, setBannerFile]           = useState(null);
  const [bannerTitle, setBannerTitle]         = useState("");
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerPreview = usePreview(bannerFile);

  // Result Images
  const [resultImages, setResultImages]       = useState([]);
  const [resImgFile, setResImgFile]           = useState(null);
  const [resImgTitle, setResImgTitle]         = useState("");
  const [resImgUploading, setResImgUploading] = useState(false);
  const resImgPreview = usePreview(resImgFile);

  // Home Hero (multiple slides)
  const [heroSlides, setHeroSlides]       = useState([]);
  const [heroFiles, setHeroFiles]         = useState([]);
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroProgress, setHeroProgress]   = useState("");

  // About Hero
  const [aboutHeroUrl, setAboutHeroUrl]     = useState("");
  const [aboutFile, setAboutFile]           = useState(null);
  const [aboutUploading, setAboutUploading] = useState(false);
  const aboutPreview = usePreview(aboutFile);

  // Admission Banner
  const [admBannerUrl, setAdmBannerUrl]             = useState("");
  const [admBannerHeading, setAdmBannerHeading]     = useState("");
  const [admBannerFile, setAdmBannerFile]           = useState(null);
  const [admBannerUploading, setAdmBannerUploading] = useState(false);
  const admBannerPreview = usePreview(admBannerFile);

  // Popup
  const [popupInfo, setPopupInfo]       = useState(null);
  const [popupEnabled, setPopupEnabled] = useState(true);
  const [popupTitle, setPopupTitle]     = useState("");
  const [popupText, setPopupText]       = useState("");
  const [popupBtnText, setPopupBtnText] = useState("Apply Now");
  const [popupBtnLink, setPopupBtnLink] = useState("/admissions");
  const [popupFile, setPopupFile]       = useState(null);
  const [popupSaving, setPopupSaving]   = useState(false);
  const popupPreview = usePreview(popupFile);

  // Videos
  const [videos, setVideos]                 = useState([]);
  const [videoFile, setVideoFile]           = useState(null);
  const [videoTitle, setVideoTitle]         = useState("");
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress]   = useState(0);
  const videoPreview = usePreview(videoFile);

  // Team
  const [team, setTeam]                   = useState([]);
  const [teamFile, setTeamFile]           = useState(null);
  const [teamName, setTeamName]           = useState("");
  const [teamRole, setTeamRole]           = useState("");
  const [teamOrder, setTeamOrder]         = useState("");
  const [editTeamId, setEditTeamId]       = useState(null);
  const [teamUploading, setTeamUploading] = useState(false);
  const teamPreview = usePreview(teamFile);

  // Alumni
  const [alumni, setAlumni]                   = useState([]);
  const [alumniFile, setAlumniFile]           = useState(null);
  const [alumniName, setAlumniName]           = useState("");
  const [alumniRole, setAlumniRole]           = useState("");
  const [alumniFeedback, setAlumniFeedback]   = useState("");
  const [alumniOrder, setAlumniOrder]         = useState("");
  const [editAlumniId, setEditAlumniId]       = useState(null);
  const [alumniUploading, setAlumniUploading] = useState(false);
  const alumniPreview = usePreview(alumniFile);

  // Careers
  const [careers, setCareers]                     = useState([]);
  const [careerTitle, setCareerTitle]             = useState("");
  const [careerCategory, setCareerCategory]       = useState("Teaching");
  const [careerDepartment, setCareerDepartment]   = useState("");
  const [careerExperience, setCareerExperience]   = useState("");
  const [careerLocation, setCareerLocation]       = useState("");
  const [careerDescription, setCareerDescription] = useState("");
  const [editCareerId, setEditCareerId]           = useState(null);
  const [careerSaving, setCareerSaving]           = useState(false);

  // Applications (received from the Careers page)
  const [applications, setApplications] = useState([]);

  const navigate = useNavigate();

  // ─────────────────────────────
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  // ─────────────────────────────
  // Firestore listeners
  useEffect(() => {
    const unsubs = [
      onSnapshot(collection(db, "admissions"), (s) => setAdmissions(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "news"),        (s) => setNews(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "results"),     (s) => setResults(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "ticker"),      (s) => setTicker(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "gallery"),     (s) => setGallery(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "campus"),      (s) => setCampus(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "schoolLife"),  (s) => setSchoolLife(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "banners"),     (s) => setBanners(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "resultImages"),(s) => setResultImages(s.docs.map((d) => ({ id: d.id, ...d.data() })))),
      onSnapshot(collection(db, "videos"),      (s) => setVideos(s.docs.map((d) => ({ id: d.id, ...d.data() })))),

      onSnapshot(collection(db, "homeHeroes"), (s) => {
        const list = s.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setHeroSlides(list);
      }, (e) => console.error("HomeHeroes:", e.message)),

      onSnapshot(collection(db, "careers"), (s) => {
        setCareers(sortByCreatedDesc(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
      }, (e) => console.error("Careers:", e.message)),

      onSnapshot(collection(db, "applications"), (s) => {
        setApplications(sortByCreatedDesc(s.docs.map((d) => ({ id: d.id, ...d.data() }))));
      }, (e) => console.error("Applications:", e.message)),

      onSnapshot(collection(db, "fees"), (s) => {
        const list = s.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setFees(list);
      }, (e) => console.error("Fees:", e.message)),

      onSnapshot(collection(db, "team"), (s) => {
        const list = s.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setTeam(list);
      }, (e) => console.error("Team:", e.message)),

      onSnapshot(collection(db, "alumni"), (s) => {
        const list = s.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
        setAlumni(list);
      }, (e) => console.error("Alumni:", e.message)),

      onSnapshot(doc(db, "settings", "admissionForm"),
        (s) => setFormInfo(s.exists() ? s.data() : null),
        (e) => console.error("AdmForm:", e.message)),

      onSnapshot(doc(db, "settings", "admissions"), (s) => {
        if (s.exists()) setAdmissionsOpen(s.data().isOpen);
        else setDoc(doc(db, "settings", "admissions"), { isOpen: true });
      }, (e) => console.error("Admissions:", e.message)),

      onSnapshot(doc(db, "settings", "aboutHero"),
        (s) => setAboutHeroUrl(s.exists() ? s.data().imageUrl || "" : ""),
        (e) => console.error("AboutHero:", e.message)),

      onSnapshot(doc(db, "settings", "admissionBanner"), (s) => {
        if (s.exists()) {
          setAdmBannerUrl(s.data().imageUrl || "");
          setAdmBannerHeading(s.data().heading || "");
        } else {
          setAdmBannerUrl("");
          setAdmBannerHeading("");
        }
      }, (e) => console.error("AdmBanner:", e.message)),

      onSnapshot(doc(db, "settings", "admissionPopup"), (s) => {
        const d = s.exists() ? s.data() : null;
        setPopupInfo(d);
        if (d) {
          setPopupEnabled(!!d.enabled);
          setPopupTitle(d.title || "");
          setPopupText(d.text || "");
          setPopupBtnText(d.buttonText || "");
          setPopupBtnLink(d.buttonLink || "");
        }
      }, (e) => console.error("Popup:", e.message)),
    ];
    return () => unsubs.forEach((u) => u());
  }, []);

  // ─────────────────────────────
  // Admissions
  const toggleAdmissions = async () => {
    try {
      await setDoc(doc(db, "settings", "admissions"), { isOpen: !admissionsOpen });
    } catch (e) { alert("❌ " + e.message); }
  };

  const exportAdmissionsExcel = () => {
    if (!admissions.length) return alert("No admissions found!");
    const data = admissions.map((a) => ({
      Name: a.name || "", Email: a.email || "", Phone: a.phone || "—",
      Course: a.course || "", Message: a.message || "",
      "Created At": a.createdAt?.toDate ? a.createdAt.toDate().toLocaleString() : "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Admissions");
    saveAs(
      new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], { type: "application/octet-stream" }),
      "admissions.xlsx"
    );
  };

  const deleteAdmission = async (id) => {
    if (window.confirm("Delete this admission?"))
      await deleteDoc(doc(db, "admissions", id)).catch((e) => alert("❌ " + e.message));
  };

  // ─── Fees ───
  const resetFeeForm = () => {
    setFeeClass(""); setFeeAdmission(""); setFeeMonthly(""); setFeeOrder(""); setEditFeeId(null);
  };
  const saveFee = async () => {
    if (!feeClass.trim()) return alert("Enter class name");
    if (feeAdmission === "" || feeMonthly === "") return alert("Enter fees");
    if (isNaN(Number(feeAdmission)) || isNaN(Number(feeMonthly))) return alert("Fees must be numbers");
    const maxOrder = fees.reduce((m, f) => Math.max(m, Number(f.order) || 0), 0);
    const data = {
      className: feeClass.trim(),
      admissionFee: Number(feeAdmission),
      monthlyFee: Number(feeMonthly),
      order: feeOrder !== "" ? Number(feeOrder) : maxOrder + 1,
    };
    try {
      setFeeSaving(true);
      if (editFeeId) await updateDoc(doc(db, "fees", editFeeId), data);
      else await addDoc(collection(db, "fees"), { ...data, createdAt: serverTimestamp() });
      resetFeeForm();
    } catch (e) { alert("❌ " + e.message); } finally { setFeeSaving(false); }
  };
  const editFee = (f) => {
    setFeeClass(f.className || "");
    setFeeAdmission(String(f.admissionFee ?? ""));
    setFeeMonthly(String(f.monthlyFee ?? ""));
    setFeeOrder(f.order !== undefined ? String(f.order) : "");
    setEditFeeId(f.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deleteFee = async (id) => {
    if (window.confirm("Delete fee row?")) {
      await deleteDoc(doc(db, "fees", id)).catch((e) => alert("❌ " + e.message));
      if (editFeeId === id) resetFeeForm();
    }
  };
  const importDefaultFees = async () => {
    if (fees.length > 0) return alert("Delete existing fees first.");
    if (!window.confirm(`Import ${DEFAULT_FEES.length} default classes?`)) return;
    try {
      setFeeSaving(true);
      const batch = writeBatch(db);
      DEFAULT_FEES.forEach(([className, admissionFee, monthlyFee], i) => {
        batch.set(doc(collection(db, "fees")), {
          className, admissionFee, monthlyFee, order: i + 1, createdAt: serverTimestamp(),
        });
      });
      await batch.commit();
      alert("✅ Default fees imported");
    } catch (e) { alert("❌ " + e.message); } finally { setFeeSaving(false); }
  };

  // ─── Admission Form ───
  const saveAdmissionForm = async () => {
    const problem = validateDocument(formFile);
    if (problem) return alert(problem);
    try {
      setFormUploading(true);
      const { url, publicId } = await uploadDocumentToCloudinary(formFile);
      await setDoc(doc(db, "settings", "admissionForm"), {
        fileUrl: url, publicId, fileName: formFile.name, fileSize: formFile.size, updatedAt: serverTimestamp(),
      });
      setFormFile(null);
      document.getElementById("admform-file").value = "";
      alert("✅ Form updated");
    } catch (e) { alert("❌ " + e.message); } finally { setFormUploading(false); }
  };
  const removeAdmissionForm = async () => {
    if (window.confirm("Remove form?"))
      await deleteDoc(doc(db, "settings", "admissionForm")).catch((e) => alert("❌ " + e.message));
  };

  // ─── News ───
  const saveNews = async () => {
    if (!newNews.trim()) return alert("Enter news title");
    try {
      if (editNewsId) {
        await updateDoc(doc(db, "news", editNewsId), { title: newNews.trim() });
        setEditNewsId(null);
      } else {
        await addDoc(collection(db, "news"), { title: newNews.trim(), createdAt: serverTimestamp() });
      }
      setNewNews("");
    } catch (e) { alert("❌ " + e.message); }
  };
  const deleteNews = async (id) => {
    if (window.confirm("Delete news?"))
      await deleteDoc(doc(db, "news", id)).catch((e) => alert("❌ " + e.message));
  };

  // ─── Results ───
  const resetResultForm = () => {
    setResYear(""); setResName(""); setResRoll(""); setResMarks(""); setEditResultId(null);
  };
  const saveResult = async () => {
    if (!resYear.trim() || !resName.trim() || !resRoll.trim() || !resMarks.trim()) return alert("Fill all fields");
    const data = {
      year: resYear.trim(), name: resName.trim(), rollNumber: resRoll.trim(), marks: resMarks.trim(),
    };
    try {
      if (editResultId) await updateDoc(doc(db, "results", editResultId), data);
      else await addDoc(collection(db, "results"), { ...data, createdAt: serverTimestamp() });
      resetResultForm();
    } catch (e) { alert("❌ " + e.message); }
  };
  const editResult = (r) => {
    setResYear(r.year || ""); setResName(r.name || ""); setResRoll(r.rollNumber || "");
    setResMarks(r.marks || ""); setEditResultId(r.id);
  };
  const deleteResult = async (id) => {
    if (window.confirm("Delete result?"))
      await deleteDoc(doc(db, "results", id)).catch((e) => alert("❌ " + e.message));
  };

  // ─── Ticker ───
  const saveTicker = async () => {
    if (!newTicker.trim()) return alert("Enter ticker text");
    try {
      if (editTickerId) {
        await updateDoc(doc(db, "ticker", editTickerId), { text: newTicker.trim() });
        setEditTickerId(null);
      } else {
        await addDoc(collection(db, "ticker"), { text: newTicker.trim(), createdAt: serverTimestamp() });
      }
      setNewTicker("");
    } catch (e) { alert("❌ " + e.message); }
  };
  const deleteTicker = async (id) => {
    if (window.confirm("Delete ticker?"))
      await deleteDoc(doc(db, "ticker", id)).catch((e) => alert("❌ " + e.message));
  };

  // ─── Careers ───
  const resetCareerForm = () => {
    setCareerTitle("");
    setCareerCategory("Teaching");
    setCareerDepartment("");
    setCareerExperience("");
    setCareerLocation("");
    setCareerDescription("");
    setEditCareerId(null);
  };

  const saveCareer = async () => {
    if (!careerTitle.trim()) return alert("Enter job title");
    if (!CAREER_CATEGORIES.includes(careerCategory)) return alert("Select a valid category");
    if (!careerDescription.trim()) return alert("Enter job description");

    const data = {
      title: careerTitle.trim(),
      category: careerCategory,
      department: careerDepartment.trim(),
      experience: careerExperience.trim(),
      location: careerLocation.trim() || DEFAULT_CAREER_LOCATION,
      description: careerDescription.trim(),
    };

    try {
      setCareerSaving(true);
      if (editCareerId) {
        await updateDoc(doc(db, "careers", editCareerId), { ...data, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, "careers"), { ...data, createdAt: serverTimestamp() });
      }
      resetCareerForm();
      alert("✅ Job saved");
    } catch (e) {
      alert("❌ " + e.message);
    } finally {
      setCareerSaving(false);
    }
  };

  const editCareer = (j) => {
    setCareerTitle(j.title || "");
    setCareerCategory(j.category || "Teaching");
    setCareerDepartment(j.department || "");
    setCareerExperience(j.experience || "");
    setCareerLocation(j.location || "");
    setCareerDescription(j.description || "");
    setEditCareerId(j.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteCareer = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await deleteDoc(doc(db, "careers", id)).catch((e) => alert("❌ " + e.message));
    if (editCareerId === id) resetCareerForm();
  };

  // ─── Applications ───
  const deleteApplication = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await deleteDoc(doc(db, "applications", id)).catch((e) => alert("❌ " + e.message));
  };

  // Gmail compose link (opens Gmail in the browser with To/Subject/Body pre-filled)
  const replyLink = (a) => {
    const subject = encodeURIComponent(`Re: Application for ${a.jobTitle || "position"}`);
    const body = encodeURIComponent(
      `Dear ${a.name},\n\nThank you for applying at Academic Group of Schools & College.\n\nRegards,\nAcademic Group of Schools & College`
    );
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(a.email)}&su=${subject}&body=${body}`;
  };

  // ─── Generic image upload helper ───
  const uploadImage = async (file, collectionName, extraData, setUploadingFn, fileInputId, resetFn) => {
    try {
      setUploadingFn(true);
      const { url, publicId } = await uploadImageToCloudinary(file);
      await addDoc(collection(db, collectionName), {
        imageUrl: url, publicId, createdAt: serverTimestamp(), ...extraData,
      });
      const el = document.getElementById(fileInputId);
      if (el) el.value = "";
      resetFn();
    } catch (e) {
      alert("❌ " + e.message);
    } finally {
      setUploadingFn(false);
    }
  };

  // Gallery
  const saveGalleryImage = () => {
    if (!galleryFile) return alert("Select an image");
    uploadImage(galleryFile, "gallery", { title: galleryTitle.trim() }, setUploading, "gallery-file", () => {
      setGalleryFile(null); setGalleryTitle("");
    });
  };
  const deleteGalleryImage = async (id) => {
    if (window.confirm("Delete image?"))
      await deleteDoc(doc(db, "gallery", id)).catch((e) => alert("❌ " + e.message));
  };

  // Campus
  const saveCampusImage = () => {
    if (!campusTitle.trim()) return alert("Enter title");
    if (!campusFile) return alert("Select an image");
    uploadImage(campusFile, "campus", { title: campusTitle.trim() }, setCampusUploading, "campus-file", () => {
      setCampusFile(null); setCampusTitle("");
    });
  };
  const deleteCampusImage = async (id) => {
    if (window.confirm("Delete?"))
      await deleteDoc(doc(db, "campus", id)).catch((e) => alert("❌ " + e.message));
  };

  // School Life
  const saveLifeImage = () => {
    if (!lifeFile) return alert("Select an image");
    uploadImage(lifeFile, "schoolLife", { title: lifeTitle.trim() }, setLifeUploading, "life-file", () => {
      setLifeFile(null); setLifeTitle("");
    });
  };
  const deleteLifeImage = async (id) => {
    if (window.confirm("Delete?"))
      await deleteDoc(doc(db, "schoolLife", id)).catch((e) => alert("❌ " + e.message));
  };

  // Banner
  const saveBanner = () => {
    if (!bannerFile) return alert("Select a banner image");
    uploadImage(bannerFile, "banners", { title: bannerTitle.trim() }, setBannerUploading, "banner-file", () => {
      setBannerFile(null); setBannerTitle("");
    });
  };
  const deleteBanner = async (id) => {
    if (window.confirm("Delete banner?"))
      await deleteDoc(doc(db, "banners", id)).catch((e) => alert("❌ " + e.message));
  };

  // Result Images
  const saveResultImage = () => {
    if (!resImgFile) return alert("Select an image");
    uploadImage(resImgFile, "resultImages", { title: resImgTitle.trim() }, setResImgUploading, "resimg-file", () => {
      setResImgFile(null); setResImgTitle("");
    });
  };
  const deleteResultImage = async (id) => {
    if (window.confirm("Delete?"))
      await deleteDoc(doc(db, "resultImages", id)).catch((e) => alert("❌ " + e.message));
  };

  // ─── Home Hero (multiple) ───
  const saveHomeHeroes = async () => {
    if (!heroFiles.length) return alert("Select at least one image");
    const bad = heroFiles.find((f) => !f.type.startsWith("image/"));
    if (bad) return alert(`"${bad.name}" image file nahi hai`);
    try {
      setHeroUploading(true);
      let order = heroSlides.reduce((m, h) => Math.max(m, Number(h.order) || 0), 0);
      for (let i = 0; i < heroFiles.length; i++) {
        setHeroProgress(`${i + 1}/${heroFiles.length}`);
        const { url, publicId } = await uploadImageToCloudinary(heroFiles[i]);
        order += 1;
        await addDoc(collection(db, "homeHeroes"), {
          imageUrl: url, publicId, order, createdAt: serverTimestamp(),
        });
      }
      setHeroFiles([]);
      const el = document.getElementById("homehero-file");
      if (el) el.value = "";
      alert("✅ Hero images uploaded");
    } catch (e) {
      alert("❌ " + e.message);
    } finally {
      setHeroUploading(false);
      setHeroProgress("");
    }
  };

  const deleteHomeHero = async (id) => {
    if (window.confirm("Delete this hero image?"))
      await deleteDoc(doc(db, "homeHeroes", id)).catch((e) => alert("❌ " + e.message));
  };

  // Order change: index ko ek position upar (dir = -1) ya neeche (dir = 1) move karo
  const moveHomeHero = async (index, dir) => {
    const cur = heroSlides[index];
    const other = heroSlides[index + dir];
    if (!cur || !other) return;
    const curOrder = Number(cur.order) || index + 1;
    const otherOrder = Number(other.order) || index + dir + 1;
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "homeHeroes", cur.id), { order: otherOrder });
      batch.update(doc(db, "homeHeroes", other.id), { order: curOrder });
      await batch.commit();
    } catch (e) {
      alert("❌ " + e.message);
    }
  };

  // About Hero
  const saveAboutHero = async () => {
    if (!aboutFile) return alert("Select an image");
    try {
      setAboutUploading(true);
      const { url, publicId } = await uploadImageToCloudinary(aboutFile);
      await setDoc(doc(db, "settings", "aboutHero"), { imageUrl: url, publicId, updatedAt: serverTimestamp() });
      setAboutFile(null);
      const el = document.getElementById("abouthero-file");
      if (el) el.value = "";
      alert("✅ Hero image updated");
    } catch (e) { alert("❌ " + e.message); } finally { setAboutUploading(false); }
  };
  const removeAboutHero = async () => {
    if (window.confirm("Remove hero image?"))
      await deleteDoc(doc(db, "settings", "aboutHero")).catch((e) => alert("❌ " + e.message));
  };

  // Admission Banner
  const saveAdmissionBanner = async () => {
    if (!admBannerFile && !admBannerUrl && !admBannerHeading.trim())
      return alert("Select image or enter heading");
    try {
      setAdmBannerUploading(true);
      let imageUrl = admBannerUrl;
      let publicId = "";
      if (admBannerFile) {
        const r = await uploadImageToCloudinary(admBannerFile);
        imageUrl = r.url;
        publicId = r.publicId;
      }
      const data = { heading: admBannerHeading.trim(), imageUrl, updatedAt: serverTimestamp() };
      if (publicId) data.publicId = publicId;
      await setDoc(doc(db, "settings", "admissionBanner"), data, { merge: true });
      setAdmBannerFile(null);
      const el = document.getElementById("admbanner-file");
      if (el) el.value = "";
      alert("✅ Banner updated");
    } catch (e) { alert("❌ " + e.message); } finally { setAdmBannerUploading(false); }
  };
  const removeAdmissionBanner = async () => {
    if (window.confirm("Reset banner?")) {
      await deleteDoc(doc(db, "settings", "admissionBanner")).catch((e) => alert("❌ " + e.message));
      setAdmBannerFile(null);
    }
  };

  // Popup
  const savePopup = async () => {
    if (!popupFile && !popupInfo?.imageUrl && !popupTitle.trim() && !popupText.trim())
      return alert("Add image or title/text");
    try {
      setPopupSaving(true);
      let imageUrl = popupInfo?.imageUrl || "";
      let publicId = popupInfo?.publicId || "";
      if (popupFile) {
        const r = await uploadImageToCloudinary(popupFile);
        imageUrl = r.url;
        publicId = r.publicId;
      }
      await setDoc(doc(db, "settings", "admissionPopup"), {
        enabled: popupEnabled,
        title: popupTitle.trim(),
        text: popupText.trim(),
        buttonText: popupBtnText.trim(),
        buttonLink: popupBtnLink.trim(),
        imageUrl,
        publicId,
        updatedAt: serverTimestamp(),
      });
      setPopupFile(null);
      const el = document.getElementById("popup-file");
      if (el) el.value = "";
      alert(`✅ Popup saved (on website: ${popupEnabled ? "ON" : "OFF"})`);
    } catch (e) { alert("❌ " + e.message); } finally { setPopupSaving(false); }
  };
  const removePopupImage = async () => {
    if (!window.confirm("Remove popup image?")) return;
    // setDoc with merge works even if the popup doc does not exist yet
    await setDoc(
      doc(db, "settings", "admissionPopup"),
      { imageUrl: "", publicId: "", updatedAt: serverTimestamp() },
      { merge: true }
    ).catch((e) => alert("❌ " + e.message));
  };

  // Videos
  const saveVideo = async () => {
    if (!videoFile) return alert("Select a video");
    if (!videoFile.type.startsWith("video/")) return alert("Select a video file");
    if (videoFile.size > MAX_VIDEO_MB * 1024 * 1024) return alert(`Max ${MAX_VIDEO_MB} MB`);
    try {
      setVideoUploading(true);
      setVideoProgress(0);
      const { url, publicId } = await uploadVideoToCloudinary(videoFile, setVideoProgress);
      await addDoc(collection(db, "videos"), {
        title: videoTitle.trim(), videoUrl: url, publicId, createdAt: serverTimestamp(),
      });
      setVideoFile(null);
      setVideoTitle("");
      const el = document.getElementById("video-file");
      if (el) el.value = "";
      alert("✅ Video uploaded");
    } catch (e) {
      alert("❌ " + e.message);
    } finally {
      setVideoUploading(false);
      setVideoProgress(0);
    }
  };
  const deleteVideo = async (id) => {
    if (window.confirm("Delete video?"))
      await deleteDoc(doc(db, "videos", id)).catch((e) => alert("❌ " + e.message));
  };

  // ─── Team CRUD ───
  const resetTeamForm = () => {
    setTeamName(""); setTeamRole(""); setTeamOrder(""); setTeamFile(null); setEditTeamId(null);
    const el = document.getElementById("team-file");
    if (el) el.value = "";
  };
  const saveTeam = async () => {
    if (!teamName.trim()) return alert("Enter name");
    if (!teamRole.trim()) return alert("Enter role");
    if (!teamFile && !editTeamId) return alert("Select an image");
    const maxOrder = team.reduce((m, t) => Math.max(m, Number(t.order) || 0), 0);
    const orderVal = teamOrder !== "" ? Number(teamOrder) : maxOrder + 1;
    try {
      setTeamUploading(true);
      let imageUrl = "";
      let publicId = "";
      if (teamFile) {
        const r = await uploadImageToCloudinary(teamFile);
        imageUrl = r.url;
        publicId = r.publicId;
      }
      if (editTeamId) {
        const data = { name: teamName.trim(), role: teamRole.trim(), order: orderVal };
        if (imageUrl) { data.imageUrl = imageUrl; data.publicId = publicId; }
        await updateDoc(doc(db, "team", editTeamId), data);
      } else {
        await addDoc(collection(db, "team"), {
          name: teamName.trim(), role: teamRole.trim(), imageUrl, publicId,
          order: orderVal, createdAt: serverTimestamp(),
        });
      }
      resetTeamForm();
      alert("✅ Team member saved");
    } catch (e) { alert("❌ " + e.message); } finally { setTeamUploading(false); }
  };
  const editTeamMember = (t) => {
    setTeamName(t.name || "");
    setTeamRole(t.role || "");
    setTeamOrder(t.order !== undefined ? String(t.order) : "");
    setEditTeamId(t.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deleteTeamMember = async (id) => {
    if (window.confirm("Delete team member?")) {
      await deleteDoc(doc(db, "team", id)).catch((e) => alert("❌ " + e.message));
      if (editTeamId === id) resetTeamForm();
    }
  };

  // ─── Alumni CRUD ───
  const resetAlumniForm = () => {
    setAlumniName(""); setAlumniRole(""); setAlumniFeedback(""); setAlumniOrder("");
    setAlumniFile(null); setEditAlumniId(null);
    const el = document.getElementById("alumni-file");
    if (el) el.value = "";
  };
  const saveAlumni = async () => {
    if (!alumniName.trim()) return alert("Enter name");
    if (!alumniRole.trim()) return alert("Enter role");
    if (!alumniFeedback.trim()) return alert("Enter feedback");
    if (!alumniFile && !editAlumniId) return alert("Select an image");
    const maxOrder = alumni.reduce((m, a) => Math.max(m, Number(a.order) || 0), 0);
    const orderVal = alumniOrder !== "" ? Number(alumniOrder) : maxOrder + 1;
    try {
      setAlumniUploading(true);
      let imageUrl = "";
      let publicId = "";
      if (alumniFile) {
        const r = await uploadImageToCloudinary(alumniFile);
        imageUrl = r.url;
        publicId = r.publicId;
      }
      if (editAlumniId) {
        const data = {
          name: alumniName.trim(), role: alumniRole.trim(),
          feedback: alumniFeedback.trim(), order: orderVal,
        };
        if (imageUrl) { data.imageUrl = imageUrl; data.publicId = publicId; }
        await updateDoc(doc(db, "alumni", editAlumniId), data);
      } else {
        await addDoc(collection(db, "alumni"), {
          name: alumniName.trim(), role: alumniRole.trim(), feedback: alumniFeedback.trim(),
          imageUrl, publicId, order: orderVal, createdAt: serverTimestamp(),
        });
      }
      resetAlumniForm();
      alert("✅ Alumni saved");
    } catch (e) { alert("❌ " + e.message); } finally { setAlumniUploading(false); }
  };
  const editAlumniMember = (a) => {
    setAlumniName(a.name || "");
    setAlumniRole(a.role || "");
    setAlumniFeedback(a.feedback || "");
    setAlumniOrder(a.order !== undefined ? String(a.order) : "");
    setEditAlumniId(a.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const deleteAlumniMember = async (id) => {
    if (window.confirm("Delete alumni?")) {
      await deleteDoc(doc(db, "alumni", id)).catch((e) => alert("❌ " + e.message));
      if (editAlumniId === id) resetAlumniForm();
    }
  };

  // ─────────────────────────────
  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Admin Panel</h2>
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      {/* ─── Tabs ─── */}
      <div className="tabs">
        {[
          ["admissions", "Admissions"], ["fees", "Fee Structure"], ["admForm", "Admission Form"],
          ["news", "News"], ["results", "Results"], ["ticker", "Ticker"], ["gallery", "Gallery"],
          ["campus", "Campus"], ["schoolLife", "School Life"], ["banners", "Banner"],
          ["resultImages", "Result Images"], ["homeHero", "Home Hero"], ["aboutHero", "About Hero"],
          ["admBanner", "Admission Banner"], ["popup", "Popup"], ["videos", "Videos"],
          ["team", "Team"], ["alumni", "Alumni"], ["careers", "Careers"],
          ["applications", "Applications"],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} className={activeTab === key ? "active" : ""}>
            {label}
          </button>
        ))}
      </div>

      {/* ───────────── Admissions ───────────── */}
      {activeTab === "admissions" && (
        <div className="section">
          <h3>Admissions ({admissions.length})</h3>
          <div className="button-group">
            <button className="toggle-btn" onClick={toggleAdmissions}>
              {admissionsOpen ? "Close Admissions" : "Open Admissions"}
            </button>
            <button className="export-btn" onClick={exportAdmissionsExcel}>Download Excel</button>
          </div>
          <ul>
            {admissions.map((a) => (
              <li key={a.id}>
                <div className="item-content">
                  <div>
                    <strong>Name:</strong> {a.name}<br />
                    <strong>Email:</strong> {a.email}<br />
                    <strong>Phone:</strong> {a.phone || "—"}<br />
                    <strong>Course:</strong> {a.course}<br />
                    <strong>Message:</strong> {a.message || "—"}<br />
                    <strong>Created At:</strong>{" "}
                    {a.createdAt?.toDate ? a.createdAt.toDate().toLocaleString() : "Not Available"}
                  </div>
                  <div className="action-buttons">
                    <button onClick={() => deleteAdmission(a.id)}>Delete</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ───────────── Fee Structure ───────────── */}
      {activeTab === "fees" && (
        <div className="section">
          <h3>Manage Fee Structure ({fees.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Rows are sorted by order number (1 is at the top).
          </p>
          <div className="add-box">
            <input type="text" placeholder="Class (e.g. Nursery)" value={feeClass} onChange={(e) => setFeeClass(e.target.value)} />
            <input type="number" min="0" placeholder="Admission Fee (PKR)" value={feeAdmission} onChange={(e) => setFeeAdmission(e.target.value)} />
            <input type="number" min="0" placeholder="Monthly Fee (PKR)" value={feeMonthly} onChange={(e) => setFeeMonthly(e.target.value)} />
            <input type="number" min="0" placeholder="Order (optional)" value={feeOrder} onChange={(e) => setFeeOrder(e.target.value)} />
            <button onClick={saveFee} disabled={feeSaving}>
              {feeSaving ? "Saving..." : editFeeId ? "Update Fee" : "Add Fee"}
            </button>
            {editFeeId && <button onClick={resetFeeForm} disabled={feeSaving}>Cancel</button>}
          </div>
          {fees.length === 0 && (
            <div style={{ margin: "14px 0" }}>
              <p style={{ color: "#777" }}>No fees yet. Import the defaults:</p>
              <button className="export-btn" onClick={importDefaultFees} disabled={feeSaving}>
                Import Default Fees
              </button>
            </div>
          )}
          {fees.length > 0 && (
            <div style={{ overflowX: "auto", marginTop: 14 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
                <thead>
                  <tr style={{ background: "#f3f4f6" }}>
                    {["Order", "Class", "Admission Fee", "Monthly Fee", "Actions"].map((h) => (
                      <th key={h} style={{ padding: 10, textAlign: "left" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fees.map((f) => (
                    <tr key={f.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: 10 }}>{f.order ?? "—"}</td>
                      <td style={{ padding: 10 }}>{f.className}</td>
                      <td style={{ padding: 10 }}>{Number(f.admissionFee).toLocaleString("en-US")}</td>
                      <td style={{ padding: 10 }}>{Number(f.monthlyFee).toLocaleString("en-US")}</td>
                      <td style={{ padding: 10 }}>
                        <div className="action-buttons">
                          <button onClick={() => editFee(f)}>Edit</button>
                          <button onClick={() => deleteFee(f.id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ───────────── Admission Form ───────────── */}
      {activeTab === "admForm" && (
        <div className="section">
          <h3>Admission Form (Download)</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>PDF, DOC, DOCX — max {MAX_FILE_MB} MB</p>
          <div className="add-box">
            <input
              id="admform-file"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => setFormFile(e.target.files[0] || null)}
              disabled={formUploading}
            />
            <button onClick={saveAdmissionForm} disabled={formUploading || !formFile}>
              {formUploading ? "Uploading..." : formInfo ? "Replace Form" : "Upload Form"}
            </button>
            {formInfo && <button onClick={removeAdmissionForm} disabled={formUploading}>Remove Form</button>}
          </div>
          {formFile && (
            <p style={{ color: "#555" }}>
              <strong>Selected:</strong> {formFile.name} ({(formFile.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
          {formInfo ? (
            <div style={{ margin: "14px 0", lineHeight: 1.8 }}>
              <strong>Current:</strong> 📄 {formInfo.fileName || "Admission Form"}<br />
              <small>Updated: {formInfo.updatedAt?.toDate ? formInfo.updatedAt.toDate().toLocaleString() : "—"}</small><br />
              <a href={formInfo.fileUrl} target="_blank" rel="noopener noreferrer">Preview / Open</a>
            </div>
          ) : (
            <p style={{ color: "#777" }}>No file uploaded. Using default /files/admission-forms.pdf</p>
          )}
        </div>
      )}

      {/* ───────────── News ───────────── */}
      {activeTab === "news" && (
        <div className="section">
          <h3>Manage News ({news.length})</h3>
          <div className="add-box">
            <input type="text" placeholder="Enter news" value={newNews} onChange={(e) => setNewNews(e.target.value)} />
            <button onClick={saveNews}>{editNewsId ? "Update News" : "Add News"}</button>
          </div>
          <ul>
            {news.map((n) => (
              <li key={n.id}>
                <div className="item-content">
                  <div>
                    {n.title}<br />
                    <small>{n.createdAt?.toDate ? n.createdAt.toDate().toLocaleString() : "No date"}</small>
                  </div>
                  <div className="action-buttons">
                    <button onClick={() => { setNewNews(n.title); setEditNewsId(n.id); }}>Edit</button>
                    <button onClick={() => deleteNews(n.id)}>Delete</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ───────────── Results ───────────── */}
      {activeTab === "results" && (
        <div className="section">
          <h3>Manage Results ({results.length})</h3>
          <div className="add-box">
            <input type="text" placeholder="Year" value={resYear} onChange={(e) => setResYear(e.target.value)} />
            <input type="text" placeholder="Student Name" value={resName} onChange={(e) => setResName(e.target.value)} />
            <input type="text" placeholder="Roll Number" value={resRoll} onChange={(e) => setResRoll(e.target.value)} />
            <input type="text" placeholder="Marks (e.g. 880/1100)" value={resMarks} onChange={(e) => setResMarks(e.target.value)} />
            <button onClick={saveResult}>{editResultId ? "Update Result" : "Add Result"}</button>
            {editResultId && <button onClick={resetResultForm}>Cancel</button>}
          </div>
          <div className="results-grid">
            {results.map((r) => (
              <div key={r.id} className="result-card">
                <div className="student-icon">🎓</div>
                <div className="result-info">
                  <h4>{r.name}</h4>
                  <p><strong>Roll:</strong> {r.rollNumber}</p>
                  <p><strong>Marks:</strong> {r.marks}</p>
                  <span className="result-year">{r.year}</span>
                </div>
                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button onClick={() => editResult(r)}>Edit</button>
                  <button onClick={() => deleteResult(r.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Ticker ───────────── */}
      {activeTab === "ticker" && (
        <div className="section">
          <h3>Manage Ticker ({ticker.length})</h3>
          <div className="add-box">
            <input type="text" placeholder="Enter ticker text" value={newTicker} onChange={(e) => setNewTicker(e.target.value)} />
            <button onClick={saveTicker}>{editTickerId ? "Update" : "Add Ticker"}</button>
          </div>
          <ul>
            {ticker.map((t) => (
              <li key={t.id}>
                <div className="item-content">
                  <div>
                    {t.text}<br />
                    <small>{t.createdAt?.toDate ? t.createdAt.toDate().toLocaleString() : "No date"}</small>
                  </div>
                  <div className="action-buttons">
                    <button onClick={() => { setNewTicker(t.text); setEditTickerId(t.id); }}>Edit</button>
                    <button onClick={() => deleteTicker(t.id)}>Delete</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ───────────── Gallery ───────────── */}
      {activeTab === "gallery" && (
        <div className="section">
          <h3>Manage Gallery ({gallery.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Landscape 3:2 (1200×800). It is auto-cropped to this size on the website.
          </p>
          <div className="add-box">
            <input type="text" placeholder="Image title (optional)" value={galleryTitle} onChange={(e) => setGalleryTitle(e.target.value)} />
            <input id="gallery-file" type="file" accept="image/*" onChange={(e) => setGalleryFile(e.target.files[0] || null)} />
            <button onClick={saveGalleryImage} disabled={uploading}>{uploading ? "Uploading..." : "Upload Image"}</button>
          </div>
          {galleryPreview && (
            <img src={galleryPreview} alt="preview" style={{ width: 150, borderRadius: 8, margin: "10px 0" }} />
          )}
          <div className="results-grid">
            {gallery.map((g) => (
              <div key={g.id} className="result-card">
                <img src={sizedUrl(g.imageUrl, "gallery")} alt={g.title || "gallery"} style={{ width: "100%", borderRadius: 8 }} />
                {g.title && <h4>{g.title}</h4>}
                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button onClick={() => deleteGalleryImage(g.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Campus ───────────── */}
      {activeTab === "campus" && (
        <div className="section">
          <h3>Manage Campus Images ({campus.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Landscape 3:2 (1200×800). It is auto-cropped to this size on the website.
          </p>
          <div className="add-box">
            <input type="text" placeholder="Campus title (e.g. Main Campus)" value={campusTitle} onChange={(e) => setCampusTitle(e.target.value)} />
            <input id="campus-file" type="file" accept="image/*" onChange={(e) => setCampusFile(e.target.files[0] || null)} />
            <button onClick={saveCampusImage} disabled={campusUploading}>{campusUploading ? "Uploading..." : "Upload"}</button>
          </div>
          {campusPreview && (
            <img src={campusPreview} alt="preview" style={{ width: 150, borderRadius: 8, margin: "10px 0" }} />
          )}
          <div className="results-grid">
            {campus.map((c) => (
              <div key={c.id} className="result-card">
                <img src={sizedUrl(c.imageUrl, "campus")} alt={c.title || "campus"} />
                {c.title && <h4>{c.title}</h4>}
                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button onClick={() => deleteCampusImage(c.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── School Life ───────────── */}
      {activeTab === "schoolLife" && (
        <div className="section">
          <h3>Manage School Life Images ({schoolLife.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Landscape 3:2 (1200×800). It is auto-cropped to this size on the website.
          </p>
          <div className="add-box">
            <input type="text" placeholder="Image title (optional)" value={lifeTitle} onChange={(e) => setLifeTitle(e.target.value)} />
            <input id="life-file" type="file" accept="image/*" onChange={(e) => setLifeFile(e.target.files[0] || null)} />
            <button onClick={saveLifeImage} disabled={lifeUploading}>{lifeUploading ? "Uploading..." : "Upload"}</button>
          </div>
          {lifePreview && (
            <img src={lifePreview} alt="preview" style={{ width: 150, borderRadius: 8, margin: "10px 0" }} />
          )}
          <div className="results-grid">
            {schoolLife.map((l) => (
              <div key={l.id} className="result-card">
                <img src={sizedUrl(l.imageUrl, "schoolLife")} alt={l.title || "school life"} />
                {l.title && <h4>{l.title}</h4>}
                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button onClick={() => deleteLifeImage(l.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Banner ───────────── */}
      {activeTab === "banners" && (
        <div className="section">
          <h3>Manage Banners ({banners.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Size: 1920×600 (3:1). It is auto-cropped to this size on the website.
          </p>
          <div className="add-box">
            <input type="text" placeholder="Banner title (optional)" value={bannerTitle} onChange={(e) => setBannerTitle(e.target.value)} />
            <input id="banner-file" type="file" accept="image/*" onChange={(e) => setBannerFile(e.target.files[0] || null)} />
            <button onClick={saveBanner} disabled={bannerUploading}>{bannerUploading ? "Uploading..." : "Upload Banner"}</button>
          </div>
          {bannerPreview && (
            <img src={bannerPreview} alt="preview" style={{ width: 250, borderRadius: 8, margin: "10px 0" }} />
          )}
          <div className="results-grid">
            {banners.map((b) => (
              <div key={b.id} className="result-card">
                <img src={sizedUrl(b.imageUrl, "banner")} alt={b.title || "banner"} />
                {b.title && <h4>{b.title}</h4>}
                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button onClick={() => deleteBanner(b.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Result Images ───────────── */}
      {activeTab === "resultImages" && (
        <div className="section">
          <h3>Manage Result Images ({resultImages.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Size: 1200×900 (4:3). Good for group photos. It is auto-cropped to this size on the website.
          </p>
          <div className="add-box">
            <input type="text" placeholder="Title (e.g. Matric Toppers 2026)" value={resImgTitle} onChange={(e) => setResImgTitle(e.target.value)} />
            <input id="resimg-file" type="file" accept="image/*" onChange={(e) => setResImgFile(e.target.files[0] || null)} />
            <button onClick={saveResultImage} disabled={resImgUploading}>{resImgUploading ? "Uploading..." : "Upload"}</button>
          </div>
          {resImgPreview && (
            <img src={resImgPreview} alt="preview" style={{ width: 150, borderRadius: 8, margin: "10px 0" }} />
          )}
          <div className="results-grid">
            {resultImages.map((r) => (
              <div key={r.id} className="result-card">
                <img src={sizedUrl(r.imageUrl, "resultImages")} alt={r.title || "result"} />
                {r.title && <h4>{r.title}</h4>}
                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button onClick={() => deleteResultImage(r.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Home Hero ───────────── */}
      {activeTab === "homeHero" && (
        <div className="section">
          <h3>Home Page Hero Images ({heroSlides.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
           You can select multiple images at once. They will appear in the home page hero section and change automatically every 5 seconds. The image with order number 1 will show first. For best results, use landscape images at 1920×1080 or larger, in JPG or WebP format.
          </p>

          <div className="add-box">
            <input
              id="homehero-file"
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setHeroFiles(Array.from(e.target.files || []))}
              disabled={heroUploading}
            />
            <button onClick={saveHomeHeroes} disabled={heroUploading || !heroFiles.length}>
              {heroUploading ? `Uploading ${heroProgress}...` : `Upload ${heroFiles.length || ""} Image(s)`}
            </button>
          </div>

          {heroFiles.length > 0 && (
            <p style={{ color: "#555" }}>
              <strong>Selected:</strong> {heroFiles.length} image(s)
            </p>
          )}

          <div className="results-grid" style={{ marginTop: 14 }}>
            {heroSlides.map((h, i) => (
              <div key={h.id} className="result-card">
                <img
                  src={sizedUrl(h.imageUrl, "homeHero")}
                  alt={`Hero ${i + 1}`}
                  style={{ width: "100%", borderRadius: 8 }}
                />
                <p style={{ margin: "8px 0", fontWeight: 600 }}>#{i + 1}</p>
                <div className="action-buttons" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => moveHomeHero(i, -1)} disabled={i === 0}>↑ Up</button>
                  <button onClick={() => moveHomeHero(i, 1)} disabled={i === heroSlides.length - 1}>↓ Down</button>
                  <button onClick={() => deleteHomeHero(h.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>

          {heroSlides.length === 0 && (
            <p style={{ color: "#777" }}>No hero images. Default image use ho rahi hai.</p>
          )}
        </div>
      )}

      {/* ───────────── About Hero ───────────── */}
      {activeTab === "aboutHero" && (
        <div className="section">
          <h3>About Page Hero Image</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            1920×800 or larger, JPG/WebP. Landscape is best. It is auto-cropped to this size on the website.
          </p>
          <div className="add-box">
            <input
              id="abouthero-file"
              type="file"
              accept="image/*"
              onChange={(e) => setAboutFile(e.target.files[0] || null)}
            />
            <button onClick={saveAboutHero} disabled={aboutUploading}>
              {aboutUploading ? "Uploading..." : aboutHeroUrl ? "Replace" : "Upload"}
            </button>
            {aboutHeroUrl && <button onClick={removeAboutHero} disabled={aboutUploading}>Remove</button>}
          </div>
          {aboutPreview && (
            <img src={aboutPreview} alt="preview" style={{ width: "100%", maxWidth: 480, borderRadius: 8, marginTop: 8 }} />
          )}
          {aboutHeroUrl ? (
            <img
              src={sizedUrl(aboutHeroUrl, "aboutHero")}
              alt="current"
              style={{ width: "100%", maxWidth: 480, borderRadius: 8, marginTop: 8 }}
            />
          ) : (
            <p style={{ color: "#777" }}>No image. Default blue gradient.</p>
          )}
        </div>
      )}

      {/* ───────────── Admission Banner ───────────── */}
      {activeTab === "admBanner" && (
        <div className="section">
          <h3>About Page – Admission Banner</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Size: 1920×600 (3:1). Leave space for the heading text. It is auto-cropped to this size on the website.
          </p>
          <div className="add-box">
            <input
              type="text"
              placeholder="Heading (e.g. Admissions Open 2026)"
              value={admBannerHeading}
              onChange={(e) => setAdmBannerHeading(e.target.value)}
              disabled={admBannerUploading}
            />
            <input
              id="admbanner-file"
              type="file"
              accept="image/*"
              onChange={(e) => setAdmBannerFile(e.target.files[0] || null)}
              disabled={admBannerUploading}
            />
            <button onClick={saveAdmissionBanner} disabled={admBannerUploading}>
              {admBannerUploading ? "Saving..." : "Save Banner"}
            </button>
            {(admBannerUrl || admBannerHeading) && (
              <button onClick={removeAdmissionBanner} disabled={admBannerUploading}>Reset</button>
            )}
          </div>
          {admBannerPreview && (
            <img src={admBannerPreview} alt="preview" style={{ width: "100%", maxWidth: 480, borderRadius: 8, marginTop: 8 }} />
          )}
          {admBannerUrl ? (
            <img
              src={sizedUrl(admBannerUrl, "admBanner")}
              alt="current banner"
              style={{ width: "100%", maxWidth: 480, borderRadius: 8, marginTop: 8 }}
            />
          ) : (
            <p style={{ color: "#777" }}>No image. Default banner.</p>
          )}
        </div>
      )}

      {/* ───────────── Popup ───────────── */}
      {activeTab === "popup" && (
        <div className="section">
          <h3>Website Popup (Admissions Open)</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Size: 800×1000 (4:5, portrait). It is auto-cropped to this size on the website.
          </p>
          <p style={{ margin: "0 0 6px", color: "#555" }}>
            Website popup: <strong>{popupInfo?.enabled ? "ON ✅" : "OFF ❌"}</strong>
          </p>
          <label style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 12px" }}>
            <input type="checkbox" checked={popupEnabled} onChange={(e) => setPopupEnabled(e.target.checked)} />
            Keep popup ON
          </label>
          <div className="add-box">
            <input type="text" placeholder="Title (optional)" value={popupTitle} onChange={(e) => setPopupTitle(e.target.value)} disabled={popupSaving} />
            <input type="text" placeholder="Short text (optional)" value={popupText} onChange={(e) => setPopupText(e.target.value)} disabled={popupSaving} />
            <input type="text" placeholder="Button text (e.g. Apply Now)" value={popupBtnText} onChange={(e) => setPopupBtnText(e.target.value)} disabled={popupSaving} />
            <input type="text" placeholder="Button link (e.g. /admissions)" value={popupBtnLink} onChange={(e) => setPopupBtnLink(e.target.value)} disabled={popupSaving} />
            <input id="popup-file" type="file" accept="image/*" onChange={(e) => setPopupFile(e.target.files[0] || null)} disabled={popupSaving} />
            <button onClick={savePopup} disabled={popupSaving}>{popupSaving ? "Saving..." : "Save Popup"}</button>
            {popupInfo?.imageUrl && <button onClick={removePopupImage} disabled={popupSaving}>Remove Image</button>}
          </div>
          {popupPreview && (
            <img src={popupPreview} alt="preview" style={{ width: "100%", maxWidth: 320, borderRadius: 8, marginTop: 8 }} />
          )}
          {popupInfo?.imageUrl ? (
            <img
              src={sizedUrl(popupInfo.imageUrl, "popup")}
              alt="current popup"
              style={{ width: "100%", maxWidth: 320, borderRadius: 8, marginTop: 8 }}
            />
          ) : (
            <p style={{ color: "#777" }}>No popup image.</p>
          )}
        </div>
      )}

      {/* ───────────── Videos ───────────── */}
      {activeTab === "videos" && (
        <div className="section">
          <h3>Manage Videos ({videos.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            MP4 (H.264), max {MAX_VIDEO_MB} MB, 1080p or lower. Size: 1920×1080 (16:9).
          </p>
          <div className="add-box">
            <input type="text" placeholder="Video title (optional)" value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} disabled={videoUploading} />
            <input id="video-file" type="file" accept="video/*" onChange={(e) => setVideoFile(e.target.files[0] || null)} disabled={videoUploading} />
            <button onClick={saveVideo} disabled={videoUploading}>
              {videoUploading ? `Uploading ${videoProgress}%` : "Upload Video"}
            </button>
          </div>
          {videoUploading && (
            <div style={{ height: 10, margin: "12px 0", background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: videoProgress + "%", height: "100%", background: "#2563eb", transition: "width 0.2s" }} />
            </div>
          )}
          {videoPreview && !videoUploading && (
            <video src={videoPreview} controls playsInline style={{ width: "100%", maxWidth: 420, borderRadius: 8, marginTop: 8, background: "#000" }} />
          )}
          <div className="results-grid">
            {videos.map((v) => (
              <div key={v.id} className="result-card">
                <video
                  src={optimizeVideoUrl(v.videoUrl, 480)}
                  poster={videoPosterUrl(v.videoUrl, 400) || undefined}
                  controls
                  preload="none"
                  playsInline
                  style={{ width: "100%", borderRadius: 8, background: "#000" }}
                />
                {v.title && <h4>{v.title}</h4>}
                <div className="action-buttons" style={{ marginTop: 12 }}>
                  <button onClick={() => deleteVideo(v.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Team ───────────── */}
      {activeTab === "team" && (
        <div className="section">
          <h3>Manage Team ({team.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            These members appear in the <strong>"Meet Our Team"</strong> slider on the About page.
            They are sorted by order (1 comes first). Size: 800×800 (square). Keep the face centered.
            When editing, upload a new image or leave it empty (the old one will stay).
          </p>
          <div className="add-box">
            <input type="text" placeholder="Name (e.g. Mr Nadeem Shahzad)" value={teamName} onChange={(e) => setTeamName(e.target.value)} disabled={teamUploading} />
            <input type="text" placeholder="Role (e.g. Director)" value={teamRole} onChange={(e) => setTeamRole(e.target.value)} disabled={teamUploading} />
            <input type="number" min="1" placeholder="Order (optional)" value={teamOrder} onChange={(e) => setTeamOrder(e.target.value)} disabled={teamUploading} />
            <input id="team-file" type="file" accept="image/*" onChange={(e) => setTeamFile(e.target.files[0] || null)} disabled={teamUploading} />
            <button onClick={saveTeam} disabled={teamUploading}>
              {teamUploading ? "Saving..." : editTeamId ? "Update Member" : "Add Member"}
            </button>
            {editTeamId && <button onClick={resetTeamForm} disabled={teamUploading}>Cancel</button>}
          </div>
          {teamPreview && (
            <img
              src={teamPreview}
              alt="preview"
              style={{ width: 120, height: 120, objectFit: "cover", borderRadius: "50%", margin: "10px 0", border: "3px solid #c9a227" }}
            />
          )}
          <div className="results-grid">
            {team.map((t) => (
              <div key={t.id} className="result-card" style={{ textAlign: "center" }}>
                {t.imageUrl ? (
                  <img
                    src={sizedUrl(t.imageUrl, "team")}
                    alt={t.name}
                    style={{ width: 100, height: 100, objectFit: "cover", borderRadius: "50%", border: "3px solid #c9a227" }}
                  />
                ) : (
                  <div style={{ width: 100, height: 100, borderRadius: "50%", background: "#1e3a6e", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 28, margin: "0 auto" }}>
                    {t.name?.replace(/^(Mr|Ms|Mrs|Dr)\s+/i, "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                )}
                <h4 style={{ marginTop: 8 }}>{t.name}</h4>
                <p style={{ color: "#666", fontSize: 13 }}>{t.role}</p>
                <p style={{ color: "#999", fontSize: 12 }}>Order: {t.order ?? "—"}</p>
                <div className="action-buttons" style={{ marginTop: 8 }}>
                  <button onClick={() => editTeamMember(t)}>Edit</button>
                  <button onClick={() => deleteTeamMember(t.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Alumni ───────────── */}
      {activeTab === "alumni" && (
        <div className="section">
          <h3>Manage Alumni ({alumni.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            These members appear in the <strong>"Our Proud Alumni"</strong> slider on the About page.
            They are sorted by order. Size: 800×800 (square). Keep the face centered.
          </p>
          <div className="add-box">
            <input type="text" placeholder="Name (e.g. Shifa Fatima)" value={alumniName} onChange={(e) => setAlumniName(e.target.value)} disabled={alumniUploading} />
            <input type="text" placeholder="Role / Profession (e.g. Aviator)" value={alumniRole} onChange={(e) => setAlumniRole(e.target.value)} disabled={alumniUploading} />
            <textarea
              placeholder="Feedback / Quote"
              value={alumniFeedback}
              onChange={(e) => setAlumniFeedback(e.target.value)}
              disabled={alumniUploading}
              rows={3}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, resize: "vertical" }}
            />
            <input type="number" min="1" placeholder="Order (optional)" value={alumniOrder} onChange={(e) => setAlumniOrder(e.target.value)} disabled={alumniUploading} />
            <input id="alumni-file" type="file" accept="image/*" onChange={(e) => setAlumniFile(e.target.files[0] || null)} disabled={alumniUploading} />
            <button onClick={saveAlumni} disabled={alumniUploading}>
              {alumniUploading ? "Saving..." : editAlumniId ? "Update Alumni" : "Add Alumni"}
            </button>
            {editAlumniId && <button onClick={resetAlumniForm} disabled={alumniUploading}>Cancel</button>}
          </div>
          {alumniPreview && (
            <img
              src={alumniPreview}
              alt="preview"
              style={{ width: 100, height: 100, objectFit: "cover", borderRadius: "50%", margin: "10px 0", border: "3px solid #c9a227" }}
            />
          )}
          <div className="results-grid">
            {alumni.map((a) => (
              <div key={a.id} className="result-card" style={{ textAlign: "center" }}>
                {a.imageUrl ? (
                  <img
                    src={sizedUrl(a.imageUrl, "alumni")}
                    alt={a.name}
                    style={{ width: 90, height: 90, objectFit: "cover", borderRadius: "50%", border: "3px solid #c9a227" }}
                  />
                ) : (
                  <div style={{ width: 90, height: 90, borderRadius: "50%", background: "#c9a227", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 24, margin: "0 auto" }}>
                    {a.name?.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </div>
                )}
                <h4 style={{ marginTop: 8 }}>{a.name}</h4>
                <p style={{ color: "#1e3a6e", fontSize: 13, fontWeight: 600 }}>{a.role}</p>
                <p style={{ color: "#555", fontSize: 12, fontStyle: "italic", margin: "6px 0" }}>
                  "{a.feedback?.slice(0, 80)}{a.feedback?.length > 80 ? "…" : ""}"
                </p>
                <p style={{ color: "#999", fontSize: 12 }}>Order: {a.order ?? "—"}</p>
                <div className="action-buttons" style={{ marginTop: 8 }}>
                  <button onClick={() => editAlumniMember(a)}>Edit</button>
                  <button onClick={() => deleteAlumniMember(a.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ───────────── Careers ───────────── */}
      {activeTab === "careers" && (
        <div className="section">
          <h3>Manage Careers / Open Positions ({careers.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Jobs added here appear under "Open Positions" on the Careers page. For the category, choose only from{" "}
            <strong>Teaching, Administration, Support Staff</strong>. If you leave the location blank, the default address will be used.
          </p>

          <div className="add-box" style={{ flexDirection: "column", alignItems: "stretch" }}>
            <input
              type="text"
              placeholder="Job Title (e.g. Mathematics Teacher)"
              value={careerTitle}
              onChange={(e) => setCareerTitle(e.target.value)}
              disabled={careerSaving}
            />
            <input
              type="text"
              placeholder="Department (e.g. Senior Section)"
              value={careerDepartment}
              onChange={(e) => setCareerDepartment(e.target.value)}
              disabled={careerSaving}
            />
            <select
              value={careerCategory}
              onChange={(e) => setCareerCategory(e.target.value)}
              disabled={careerSaving}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14 }}
            >
              {CAREER_CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Experience (e.g. 2+ years)"
              value={careerExperience}
              onChange={(e) => setCareerExperience(e.target.value)}
              disabled={careerSaving}
            />
            <input
              type="text"
              placeholder="Location (optional, empty = default address)"
              value={careerLocation}
              onChange={(e) => setCareerLocation(e.target.value)}
              disabled={careerSaving}
            />
            <textarea
              placeholder="Job description / requirements"
              value={careerDescription}
              onChange={(e) => setCareerDescription(e.target.value)}
              disabled={careerSaving}
              rows={4}
              style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, resize: "vertical" }}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={saveCareer} disabled={careerSaving}>
                {careerSaving ? "Saving..." : editCareerId ? "Update Job" : "Add Job"}
              </button>
              {editCareerId && <button onClick={resetCareerForm} disabled={careerSaving}>Cancel</button>}
            </div>
          </div>

          <div className="results-grid" style={{ marginTop: 16 }}>
            {careers.map((j) => (
              <div key={j.id} className="result-card">
                <h4>{j.title}</h4>
                {j.department && (
                  <p style={{ color: "#1e3a6e", fontSize: 13, fontWeight: 600 }}>{j.department}</p>
                )}
                <p style={{ color: "#555", fontSize: 13 }}><strong>Category:</strong> {j.category || "—"}</p>
                <p style={{ color: "#555", fontSize: 13 }}><strong>Experience:</strong> {j.experience || "—"}</p>
                <p style={{ color: "#555", fontSize: 13 }}>
                  <strong>Location:</strong> {j.location || DEFAULT_CAREER_LOCATION}
                </p>
                <p style={{ color: "#666", fontSize: 13 }}>
                  {j.description?.slice(0, 120)}{j.description?.length > 120 ? "…" : ""}
                </p>
                <div className="action-buttons" style={{ marginTop: 8 }}>
                  <button onClick={() => editCareer(j)}>Edit</button>
                  <button onClick={() => deleteCareer(j.id)}>Delete</button>
                </div>
              </div>
            ))}
            {careers.length === 0 && (
              <p style={{ color: "#777" }}>There are no jobs listed yet. Please add one using the form above.</p>
            )}
          </div>
        </div>
      )}

      {/* ───────────── Applications ───────────── */}
      {activeTab === "applications" && (
        <div className="section">
          <h3>Job Applications ({applications.length})</h3>
          <p style={{ margin: "0 0 14px", color: "#555" }}>
            Applications received through the careers page. Clicking "Download CV" will open the file.
          </p>

          {applications.length === 0 && (
            <p style={{ color: "#777" }}>No applications have come in yet.</p>
          )}

          <div className="results-grid">
            {applications.map((a) => (
              <div key={a.id} className="result-card">
                <h4>{a.name}</h4>
                <p style={{ color: "#1e3a6e", fontSize: 13, fontWeight: 600 }}>{a.jobTitle || "—"}</p>
                <p style={{ color: "#555", fontSize: 13 }}><strong>Email:</strong> {a.email}</p>
                <p style={{ color: "#555", fontSize: 13 }}><strong>Phone:</strong> {a.phone || "—"}</p>
                {a.message && (
                  <p style={{ color: "#666", fontSize: 13 }}>
                    {a.message.slice(0, 120)}{a.message.length > 120 ? "…" : ""}
                  </p>
                )}
                <p style={{ color: "#999", fontSize: 12 }}>
                  {a.createdAt?.toDate ? a.createdAt.toDate().toLocaleString() : "—"}
                </p>
                <div className="action-buttons" style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {a.cvUrl && (
                    <a href={a.cvUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                      <button type="button">Download CV</button>
                    </a>
                  )}
                  <a href={replyLink(a)} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                    <button type="button">Reply (Gmail)</button>
                  </a>
                  <button type="button" onClick={() => deleteApplication(a.id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;